import type { APIKey, APIKeyListItem } from '#dn-types/api-key.ts';
import type { ResourceListOptions } from '#dn-types/api.ts';
import { rewordValidationMessages } from './errors.ts';
import { del, get, getList, post, put } from './fetch.ts';

export const DEFAULT_API_KEYS_PAGE_SIZE = 25;

// POST /api-keys
export type AddAPIKeyVariables = {
  name: string;
  permissions?: string[];
  /** RFC3339 timestamp.  Omit for a key that never expires.  Immutable after creation. */
  expiresAt?: string;
  /** Scope the key to a single role: its hosts permissions apply only to hosts with that role.  Omit for an unscoped key.  Immutable after creation. */
  roleID?: string;
};
export type AddAPIKeyResult = APIKey;
export function addAPIKey(formData: AddAPIKeyVariables) {
  return (
    post<APIKey>('/v1/api-keys', formData)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have an api key with this name, try another.';
          }
          if (details.path === 'expiresAt' && details.code === 'ERR_INVALID_VALUE') {
            return 'The expiration date must be in the future.';
          }
          if (details.path === 'roleID' && details.code === 'ERR_INVALID_REFERENCE') {
            return 'This role no longer exists, choose another.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            // If we got a limit from the api, we'll use that
            if (typeof details.extra?.limit === 'number') {
              return `You have already created the maximum number of api keys for this organization (${details.extra.limit} keys).`;
            }
            // Otherwise, we need to be more generic
            return 'You have already created the maximum number of api keys for this organization.';
          }
        });
      })
  );
}

// PUT /api-keys/:id
export type EditAPIKeyVariables = {
  id: string;
  values: {
    name: string;
    permissions?: string[];
  };
};
export type EditAPIKeyResult = APIKey;
export function editAPIKey({ id, values }: EditAPIKeyVariables) {
  return (
    put<EditAPIKeyResult>(`/v1/api-keys/${encodeURIComponent(id)}`, values)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have an api key with this name, try another.';
          }
        });
      })
  );
}

// GET /api-keys
export type GetAPIKeysResult = APIKeyListItem[];
export function getAPIKeys({ cursor = '', pageSize = DEFAULT_API_KEYS_PAGE_SIZE, init }: ResourceListOptions = {}) {
  return getList<GetAPIKeysResult>(`/v1/api-keys?cursor=${encodeURIComponent(cursor)}&pageSize=${pageSize}`, init).then(
    (res) => {
      // We're not actually paginating, otherwise we would need to do the "paginating backwards two-step shuffle"
      return res;
    }
  );
}

// GET /api-keys/:id
export type GetAPIKeyResult = APIKeyListItem;
export function getAPIKey(id: string, init?: RequestInit) {
  return get<GetAPIKeyResult>(`/v1/api-keys/${encodeURIComponent(id)}`, init);
}

// DELETE /api-keys/:id
export type DeleteAPIKeyResult = Record<string, never>;
export function deleteAPIKey(id: string, init?: RequestInit) {
  return del<DeleteAPIKeyResult>(`/v1/api-keys/${encodeURIComponent(id)}`, init);
}
